import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, MapPin, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_earnest-refunds/artifacts/246c7xmc_image%20-%202026-02-17T122000.629.jpg";

const navItems = [
  { name: "Home", path: "/" },
  { name: "California", path: "/california" },
  { name: "Arizona", path: "/arizona" },
  { name: "Georgia", path: "/georgia" },
  { name: "About Us", path: "/about" },
  { name: "FAQs", path: "/faqs" },
  { name: "Contact", path: "/contact" },
];

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="bg-slate-900 sticky top-0 z-50">
      {/* Top bar */}
      <div className="bg-slate-800 py-2">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl flex justify-between items-center text-sm">
          <div className="flex items-center gap-6 text-slate-300">
            <a href="tel:405-754-2183" className="flex items-center gap-2 hover:text-emerald-400 transition-colors" data-testid="header-phone">
              <Phone className="w-4 h-4" strokeWidth={1.5} />
              <span>405-754-2183</span>
            </a>
            <span className="hidden md:flex items-center gap-2">
              <MapPin className="w-4 h-4" strokeWidth={1.5} />
              <span>Phoenix, AZ</span>
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-slate-400 hidden sm:block">
              Integrity • Reliance • Compassion
            </span>
            <Link 
              to="/admin" 
              className="text-slate-500 hover:text-emerald-400 transition-colors"
              title="Admin Login"
              data-testid="header-admin-icon"
            >
              <Settings className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
      
      {/* Main navigation */}
      <nav className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-18 md:h-20 lg:h-[88px]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
            <img 
              src={LOGO_URL} 
              alt="Cornerstone Refund" 
              className="h-14 md:h-16 lg:h-[72px] w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-link px-4 py-2 text-sm font-medium transition-colors ${
                  location.pathname === item.path
                    ? "text-emerald-400"
                    : "text-slate-300 hover:text-white"
                }`}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link to="/contact">
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="header-get-started">
                Get Started
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden mobile-menu-enter pb-4">
            <div className="flex flex-col gap-2">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === item.path
                      ? "bg-emerald-600 text-white"
                      : "text-slate-300 hover:bg-slate-800"
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {item.name}
                </Link>
              ))}
              <Link
                to="/contact"
                className="mt-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-slate-900 text-white">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <img src={LOGO_URL} alt="Cornerstone Refund" className="h-16 w-auto mb-6" />
            <p className="text-slate-400 leading-relaxed max-w-md">
              Helping property owners recover overage funds with integrity, reliance, and compassion. 
              We're committed to guiding you through every step of the process.
            </p>
            <div className="mt-6">
              <p className="text-slate-300 font-medium">Michael Ernest, CEO</p>
              <p className="text-slate-500 text-sm italic mt-1">
                "Perseverance and determination usually wins most of the battles and are the key to success"
              </p>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold text-lg mb-4">Our Services</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/california" className="text-slate-400 hover:text-emerald-400 transition-colors">
                  California Refunds
                </Link>
              </li>
              <li>
                <Link to="/arizona" className="text-slate-400 hover:text-emerald-400 transition-colors">
                  Arizona Refunds
                </Link>
              </li>
              <li>
                <Link to="/georgia" className="text-slate-400 hover:text-emerald-400 transition-colors">
                  Georgia Refunds
                </Link>
              </li>
              <li>
                <Link to="/faqs" className="text-slate-400 hover:text-emerald-400 transition-colors">
                  FAQs
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-lg mb-4">Contact Us</h4>
            <ul className="space-y-3 text-slate-400">
              <li>
                <a href="tel:405-754-2183" className="hover:text-emerald-400 transition-colors flex items-center gap-2">
                  <Phone className="w-4 h-4" strokeWidth={1.5} />
                  405-754-2183
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-1 flex-shrink-0" strokeWidth={1.5} />
                <span>
                  2345 E. Thomas Rd Ste 100<br />
                  #510<br />
                  Phoenix, AZ 85016
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} Cornerstone Refund. All rights reserved.
          </p>
          <div className="flex flex-wrap gap-4 md:gap-6 text-sm text-slate-500 justify-center">
            <Link to="/about" className="hover:text-white transition-colors">About</Link>
            <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            <Link to="/faqs" className="hover:text-white transition-colors">FAQs</Link>
            <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms-of-service" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/admin" className="hover:text-emerald-400 transition-colors" data-testid="footer-admin-link">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children }) => {
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
};
